import Button from '@/shared/Button'
import FormTextField from '@/shared/FormCreator/FormItem/FormTextField'
import Table from '@/shared/Table'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import get from 'lodash/get'
import Papa from 'papaparse'
import Typography from '@mui/material/Typography'
import languageEncoding from 'detect-file-encoding-and-language'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { forwardRef, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormRadio from '@/shared/FormCreator/FormItem/FormRadio'
import Download from '@mui/icons-material/Download'
import { useSelector } from 'react-redux'

const CustomUploadInput = forwardRef((props, ref) => {
  const { id, className } = props
  const { value, ...inputProps } = props

  return (
    <Box sx={{ '& input': { display: 'none' }, width: '100%', display: 'flex' }}>
      <Box
        component="label"
        htmlFor={id}
        className={className}
        sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
      >
        {get(value, 'name') || ''}
      </Box>
      <input ref={ref} {...inputProps} type="file" accept=".csv" />
    </Box>
  )
})

const CsvUpload = ({ dispatch, onFileChange, simulateItem, setSimulateItem, value }) => {
  const { t } = useTranslation()
  const [tableData, setTableData] = useState({})

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const uploadRef = useRef()

  const companyId = useSelector(state => state.auth.account?.companyId)

  useEffect(() => {
    if (value) {
      Papa.parse(value, {
        header: true,
        skipEmptyLines: true,
        complete: result => {
          const { data, meta } = result
          const headers = meta.fields

          setTableData({
            cols: headers.map(header => ({ field: header })),
            data: data.map((d, i) => ({ ...d, id: i })),
          })
        },
      })
    }
  }, [value])

  const { cols = [], data = [] } = tableData

  const pagination = {
    totalPages: Math.ceil(data.length / pageSize),
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const sampleData = [
    [
      '中文名字',
      'Email',
      'phone',
      '公司名稱',
      '名',
      '姓',
      '部門',
      '事業群',
      '辦公室',
      '職位',
      '主管',
    ],
    [
      '陳曉明',
      'mingchen@gmail.com',
      '0910-1111',
      '台灣大哥大',
      '大明',
      '陳',
      '雲端數據',
      '資料分析',
      '學府1F',
      '工程師',
      '林大明',
    ],
    [
      '陳曉明',
      'mingchen@gmail.com',
      '0910-1111',
      '台灣大哥大',
      '大明',
      '陳',
      '雲端數據',
      '資料分析',
      '學府1F',
      '工程師',
      '林大明',
    ],
  ]

  const handleDownload = () => {
    const lines = sampleData.map(row => row.join(','))

    // CRLF 換行，最後一行也要有換行
    const csvData = `${lines.join('\r\n')}\r\n`

    // 加 UTF-8 BOM
    const blob = new Blob(['\uFEFF', csvData], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'SampleData.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <Stack>
      {data.length > 0 && (
        <Table
          minHeight={300}
          columns={cols}
          rows={data.slice(page * pageSize, (page + 1) * pageSize)}
          pagination={pagination}
          containerStyle={{ marginTop: '20px' }}
        />
      )}
      <Grid container alignItems="center" spacing={3} mt={1}>
        <Grid item xs={12}>
          <FormTextField
            type="file"
            itemProps={{ label: t('general.select_upload_csv') }}
            inputConfig={{
              inputComponent: CustomUploadInput,
              inputBaseProps: {
                container: false,
                spacing: undefined,
                alignItems: 'stretch',
                flexDirection: 'column',
                labelColor: 'common.black',
                labelFontWeight: 500,
                labelSx: { fontSize: 14, lineHeight: '25px' },
                labelProps: { py: 0 },
                childrenSx: { paddingTop: '10 !important', alignItems: 'center', mt: 2 },
                labelSuffix: (
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.5)',
                      fontSize: '12px',
                      ml: 1,
                      lineHeight: '16px',
                    }}
                  >
                    {t('general.file_name_restriction')}
                  </Typography>
                ),
              },
              value,
              onChange: e => {
                const file = e.target.files[0]

                if (file.type === 'text/csv') {
                  languageEncoding(file).then(info => {
                    const { encoding } = info
                    if (encoding === 'UTF-8') {
                      onFileChange?.(file)
                    } else {
                      dispatch({
                        type: 'app/notify',
                        payload: { message: t('errors.csv_format'), severity: 'error' },
                      })
                    }
                  })
                } else {
                  dispatch({
                    type: 'app/notify',
                    payload: { message: t('errors.csv_format'), severity: 'error' },
                  })
                }
              },
              fullWidth: true,
              endAdornment: () => (
                <Button
                  color={'secondary_80'}
                  size="medium"
                  sx={{
                    width: 100,
                    mr: '-14px',
                    border: theme => `1px solid ${theme.palette.secondary_80.main}`,
                    borderRadius: '0px 3px 3px 0px',
                  }}
                  onClick={() => {
                    uploadRef.current?.click()
                  }}
                >
                  {t('general.browse')}
                </Button>
              ),
              afterTextField: () =>
                data.length > 0 && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
                  >
                    <CheckCircleIcon color="primary" sx={{ ml: 2 }} />
                  </Box>
                ),
            }}
            formProps={{ field: { ref: uploadRef } }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormRadio
            itemProps={{ label: t('general.select_simulate_item') }}
            inputConfig={{
              inputBaseProps: {
                flexDirection: 'column',
                labelColor: 'common.black',
                labelFontWeight: 500,
                labelSx: { fontSize: 14, lineHeight: '25px' },
                labelProps: { py: 0 },
                childrenSx: { paddingTop: '4px !important' },
              },
              value: simulateItem,
              options: [
                { value: 'EMAIL', label: t('general.mail') },
                ...(companyId === 1
                  ? [
                      { value: 'PHONE', label: t('general.sms') },
                      { value: 'EMAILANDPHONE', label: t('general.mail_sms') },
                    ]
                  : []),
              ],
            }}
            formProps={{
              field: {
                value: simulateItem,
                onChange: (_, val) => {
                  setSimulateItem(val)
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            onClick={handleDownload}
            startIcon={<Download />}
            variant="text"
            color="secondary_80"
            sx={{ ml: -0.6 }}
          >
            {t('general.download_sample_csv')}
          </Button>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default CsvUpload
